import type {
  GreatKingdomAction,
  GreatKingdomPlayerId,
  GreatKingdomState,
  PassAction,
  PlaceCastleAction,
  PlaceNeutralAction,
} from "../types";
import {
  applyScoreEstimate,
  calculateTerritory,
  clearCells,
  findCapturedGroupsAfterMove,
  getOpponentPlayerId,
  setCell,
} from "../utils";

function withNextTurn(
  state: GreatKingdomState,
  currentPlayerId: GreatKingdomPlayerId,
): GreatKingdomState {
  return {
    ...state,
    currentPlayerId,
    moveNumber: state.moveNumber + 1,
  };
}

function withUpdatedTerritory(state: GreatKingdomState): GreatKingdomState {
  return applyScoreEstimate(state, calculateTerritory(state.board).summary);
}

export function applyPlaceNeutral(
  state: GreatKingdomState,
  action: PlaceNeutralAction,
): GreatKingdomState {
  const nextState = withUpdatedTerritory({
    ...withNextTurn(state, action.playerId),
    phase: "playing",
    board: setCell(state.board, action.row, action.col, "neutral"),
    passStreak: 0,
    lastAction: action,
  });

  return nextState;
}

export function applyPlaceCastle(
  state: GreatKingdomState,
  action: PlaceCastleAction,
): GreatKingdomState {
  const boardAfterPlacement = setCell(
    state.board,
    action.row,
    action.col,
    action.playerId,
  );
  const capturedGroups = findCapturedGroupsAfterMove(
    boardAfterPlacement,
    action.playerId,
  );
  const boardAfterCapture =
    capturedGroups.length > 0
      ? clearCells(
          boardAfterPlacement,
          capturedGroups.flatMap((group) => group.cells),
        )
      : boardAfterPlacement;
  const territory = calculateTerritory(boardAfterCapture).summary;

  if (capturedGroups.length > 0) {
    return applyScoreEstimate(
      {
        ...state,
        board: boardAfterCapture,
        phase: "game-over",
        currentPlayerId: action.playerId,
        moveNumber: state.moveNumber + 1,
        passStreak: 0,
        territory,
        capturedGroups: [...state.capturedGroups, ...capturedGroups],
        winnerId: action.playerId,
        endReason: "siege",
        lastAction: action,
      },
      territory,
    );
  }

  return applyScoreEstimate(
    {
      ...state,
      board: boardAfterPlacement,
      currentPlayerId: getOpponentPlayerId(action.playerId),
      moveNumber: state.moveNumber + 1,
      passStreak: 0,
      territory,
      lastAction: action,
    },
    territory,
  );
}

function resolveDoublePassWinner(
  territory: Readonly<Record<GreatKingdomPlayerId, number>>,
): GreatKingdomPlayerId {
  return territory.p1 - territory.p2 >= 3 ? "p1" : "p2";
}

export function applyPass(
  state: GreatKingdomState,
  action: PassAction,
): GreatKingdomState {
  const passStreak = state.passStreak + 1;
  const territory = calculateTerritory(state.board).summary;

  if (passStreak >= 2) {
    return applyScoreEstimate(
      {
        ...state,
        phase: "game-over",
        currentPlayerId: action.playerId,
        moveNumber: state.moveNumber + 1,
        passStreak,
        territory,
        winnerId: resolveDoublePassWinner(territory),
        endReason: "double-pass",
        lastAction: action,
      },
      territory,
    );
  }

  return applyScoreEstimate(
    {
      ...state,
      currentPlayerId: getOpponentPlayerId(action.playerId),
      moveNumber: state.moveNumber + 1,
      passStreak,
      territory,
      lastAction: action,
    },
    territory,
  );
}

export function applyAction(
  state: GreatKingdomState,
  action: GreatKingdomAction,
): GreatKingdomState {
  switch (action.type) {
    case "PlaceNeutral":
      return applyPlaceNeutral(state, action);
    case "PlaceCastle":
      return applyPlaceCastle(state, action);
    case "Pass":
      return applyPass(state, action);
  }
}
